// ── EmailClaudePanel (Evo Brain — panel 6, último panel) ─────────────────────
// Reemplaza el chat per-mensaje viejo (GET/POST /api/email/messages/:id/chat,
// email_chats, sin approval gate, cuerpo del email precargado en CADA turno)
// por el motor agéntico compartido — mismo patrón que Project/Contact/Lead/
// Report/BillingClaudePanel. El cuerpo del email entra al contexto solo
// cuando la tool read_email_thread se llama, nunca precargado.
//
// El composer (redactar un email nuevo) NO migra — sigue con compose-chat,
// stateless, sin cambios. Este componente es solo para el panel del email
// seleccionado en la lista.

import { useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { AssistantShell } from '../ui/AssistantShell.js';
import { ApprovalCard, type ApprovalProposalItem } from '../ui/ApprovalCard.js';
import { SafeLink } from '../SafeLink.js';
import { FollowupAnalysisPanel } from './FollowupAnalysisPanel.js';
import {
  useAssistant,
  type AssistantMessage,
} from '../../hooks/useAssistant.js';
import { useEmailDetail } from '../../hooks/useEmail.js';

// ── Agrupar mensajes tool_proposal consecutivos por turnGroupId (idéntico a
// Project/Contact/Lead/Report/BillingClaudePanel) ────────────────────────────
type RenderItem =
  | { kind: 'message'; msg: AssistantMessage }
  | { kind: 'proposalGroup'; turnGroupId: string; items: ApprovalProposalItem[] };

function groupProposals(messages: AssistantMessage[]): RenderItem[] {
  const out: RenderItem[] = [];
  for (const msg of messages) {
    if (msg.role === 'tool_proposal' && msg.proposal) {
      const item: ApprovalProposalItem = {
        id: msg.id,
        taskId: msg.proposal.taskId,
        turnGroupId: msg.proposal.turnGroupId,
        toolName: msg.toolName ?? '',
        verb: msg.proposal.verb,
        sentence: msg.proposal.summary.sentence,
        changes: msg.proposal.summary.changes,
        irreversible: msg.proposal.summary.irreversible,
        warning: msg.proposal.summary.warning,
        createdAt: msg.createdAt,
        status: msg.proposal.status,
      };
      const last = out[out.length - 1];
      if (last?.kind === 'proposalGroup' && last.turnGroupId === item.turnGroupId) {
        last.items.push(item);
        continue;
      }
      out.push({ kind: 'proposalGroup', turnGroupId: item.turnGroupId, items: [item] });
      continue;
    }
    out.push({ kind: 'message', msg });
  }
  return out;
}

// ── Sub-components (idénticos a Project/Contact/Lead/Report/BillingClaudePanel,
// con el link-blocking de phishing/spam heredado del chat viejo) ────────────

function UserBubble({ content }: { content: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--evo-space-2)' }}>
      <div style={{
        maxWidth: '85%',
        background: 'var(--evo-cyan)',
        color: 'var(--evo-fg-on-accent)',
        borderRadius: '10px 10px 2px 10px',
        padding: '6px 10px',
        fontSize: 'var(--evo-fs-label)',
        fontFamily: 'var(--evo-font)',
        lineHeight: 1.45,
        wordBreak: 'break-word',
      }}>
        {content}
      </div>
    </div>
  );
}

function AssistantBubble({ content, isRiskyEmail }: { content: string; isRiskyEmail: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 'var(--evo-space-2)' }}>
      <div style={{
        maxWidth: '90%',
        background: 'var(--evo-bg-2)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: '2px 10px 10px 10px',
        padding: '6px 10px',
        fontSize: 'var(--evo-fs-label)',
        fontFamily: 'var(--evo-font)',
        lineHeight: 1.45,
        color: 'var(--evo-fg-1)',
        wordBreak: 'break-word',
      }}>
        <div className="assistant-markdown">
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children }) => <SafeLink href={href} isBlocked={isRiskyEmail}>{children}</SafeLink>,
            }}
          >
            {content}
          </Markdown>
        </div>
      </div>
    </div>
  );
}

function ToolResultCard({
  msg,
  onUndo,
  isUndoing,
}: {
  msg: AssistantMessage;
  onUndo: (id: number) => void;
  isUndoing: boolean;
}) {
  const { t } = useTranslation();
  const isUndone = msg.isUndone === 1;

  return (
    <div style={{
      background: 'var(--evo-bg-2)',
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)',
      padding: '8px 10px',
      marginBottom: 'var(--evo-space-2)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--evo-space-2)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)',
          color: isUndone ? 'var(--evo-fg-4)' : 'var(--evo-fg-3)',
          letterSpacing: 'var(--evo-ls-caps)',
          textTransform: 'uppercase',
          textDecoration: isUndone ? 'line-through' : 'none',
        }}>
          <Icon name="bolt" size={10} />
          {msg.toolName ?? t('email.assistant.tool', { defaultValue: 'Tool' })}
        </div>

        {msg.undoable && !isUndone && (
          <Button
            variant="ghost"
            tone="muted"
            size="xs"
            loading={isUndoing}
            icon={<Icon name="return" size={10} />}
            onClick={() => onUndo(msg.id)}
          >
            {isUndoing
              ? t('email.assistant.undoing', { defaultValue: 'Deshaciendo…' })
              : t('email.assistant.undo', { defaultValue: 'Deshacer' })}
          </Button>
        )}

        {isUndone && (
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)' }}>
            {t('email.assistant.undone', { defaultValue: 'Deshecho' })}
          </span>
        )}
      </div>

      {msg.humanSummary && (
        <div style={{
          fontSize: 'var(--evo-fs-label)',
          fontFamily: 'var(--evo-font)',
          color: isUndone ? 'var(--evo-fg-4)' : 'var(--evo-fg-2)',
          lineHeight: 1.45,
          textDecoration: isUndone ? 'line-through' : 'none',
        }}>
          {msg.humanSummary}
        </div>
      )}
    </div>
  );
}

function ThinkingIndicator() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0', marginBottom: 'var(--evo-space-2)' }}>
      <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: '5px', height: '5px', borderRadius: '50%', background: 'var(--evo-fg-4)',
            animation: `assistant-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </span>
      <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)' }}>
        {t('email.assistant.thinking', { defaultValue: 'Pensando…' })}
      </span>
    </div>
  );
}

function ExecutingIndicator({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px',
      background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)', marginBottom: 'var(--evo-space-2)',
    }}>
      <Icon name="bolt" size={11} />
      <span style={{
        fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {t('email.assistant.executing', { defaultValue: 'Ejecutando: {{label}}…', label })}
      </span>
      <span style={{
        width: '10px', height: '10px', borderRadius: '50%', border: '2px solid var(--evo-stroke)',
        borderTopColor: 'var(--evo-accent)', animation: 'spin 0.6s linear infinite', flexShrink: 0,
      }} />
    </div>
  );
}

let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes assistant-dot {
      0%, 60%, 100% { opacity: 0.2; transform: scale(0.85); }
      30% { opacity: 1; transform: scale(1.15); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  messageId: number | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EmailClaudePanel({ messageId }: Props) {
  const { t } = useTranslation();
  injectKeyframes();

  const assistant = useAssistant('email_message', messageId != null ? String(messageId) : null);
  const { data: emailData } = useEmailDetail(messageId);
  const isRiskyEmail = emailData?.detail.classifiedAs === 'Phishing' || emailData?.detail.classifiedAs === 'Spam Sugerido';

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [assistant.messages.length, assistant.streaming.phase]);

  const isStreaming = assistant.streaming.phase !== 'idle';

  const visibleMessages = assistant.messages.filter(m => m.role !== 'tool_call');
  const renderItems = useMemo(() => groupProposals(visibleMessages), [visibleMessages]);

  const emailEntity = messageId != null
    ? { label: emailData?.detail.subject ?? `#${messageId}`, href: `/email?id=${messageId}` }
    : null;

  return (
    <AssistantShell
      muted={assistant.muted}
      onToggleMute={assistant.toggleMuted}
      isSpeaking={assistant.isSpeaking}
      onStopSpeaking={assistant.stopSpeaking}
      ttsBroken={assistant.ttsBroken}
      ttsErrorMessage={assistant.ttsErrorMessage}
      onReactivateTts={assistant.reactivateTts}
      voiceNote={assistant.voiceNote}
      storageKey="evo-email-assistant-collapsed"
      title={t('email.assistant.title')}
      topContent={messageId != null && emailData?.detail.direction === 'inbound' ? (
        <div className="px-3 pt-3">
          <FollowupAnalysisPanel messageId={messageId} detail={emailData.detail} />
        </div>
      ) : undefined}
      onSendMessage={msg => { void assistant.sendMessage(msg); }}
      isStreaming={isStreaming}
      inputDisabled={messageId == null}
      inputPlaceholder={messageId != null ? t('email.assistant.placeholder') : t('email.assistant.placeholderDisabled')}
    >
      {assistant.error != null && (
        <div style={{
          background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-sm)',
          padding: '6px 10px', marginBottom: 'var(--evo-space-3)', fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
        }}>
          {t('email.assistant.errorLabel', { defaultValue: 'Error' })}: {assistant.error}
        </div>
      )}

      {visibleMessages.length === 0 && assistant.streaming.phase === 'idle' && (
        <p style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', margin: 0, lineHeight: 1.5 }}>
          {t('email.assistant.hint')}
        </p>
      )}

      {renderItems.map(item => {
        if (item.kind === 'proposalGroup') {
          const pendingIds = item.items.filter(p => p.status === 'pending').map(p => p.taskId);
          return (
            <div key={`group-${item.turnGroupId}`} style={{ marginBottom: 'var(--evo-space-2)' }}>
              <ApprovalCard
                proposals={item.items}
                entity={emailEntity}
                onApprove={(id, remember) => { void assistant.approveProposal(id, remember); }}
                onReject={id => { void assistant.rejectProposal(id); }}
                onApproveAll={() => { void assistant.approveAllProposals(pendingIds); }}
                onRejectAll={() => { void assistant.rejectAllProposals(pendingIds); }}
                approvingId={assistant.approvingTaskId}
                rejectingId={assistant.rejectingTaskId}
                approvingAll={assistant.bulkApproving}
                rejectingAll={assistant.bulkRejecting}
              />
            </div>
          );
        }

        const msg = item.msg;
        if (msg.role === 'user') return <UserBubble key={msg.id} content={msg.content} />;
        if (msg.role === 'tool_result') {
          return (
            <ToolResultCard key={msg.id} msg={msg} onUndo={assistant.undoMessage} isUndoing={assistant.isUndoing} />
          );
        }
        return <AssistantBubble key={msg.id} content={msg.content} isRiskyEmail={isRiskyEmail} />;
      })}

      {assistant.streaming.phase === 'thinking' && <ThinkingIndicator />}
      {assistant.streaming.phase === 'tool_executing' && assistant.streaming.toolLabel != null && (
        <ExecutingIndicator label={assistant.streaming.toolLabel} />
      )}
      {assistant.streaming.phase === 'text' && assistant.streaming.textAccum && (
        <AssistantBubble content={assistant.streaming.textAccum} isRiskyEmail={isRiskyEmail} />
      )}

      <div ref={messagesEndRef} />
    </AssistantShell>
  );
}
